import { useState, useCallback, useEffect, useRef } from 'react'
import { RECONNECTING_MESSAGE, useTaskContext } from './TaskContext'
import { throwIfProfileRegenerating } from '@/api/profileRegenError'
import { useProfileRegenGuard } from '@/components/ProfileRegenBlockProvider'

const LEGACY_STARTING_MESSAGE = 'Starting matching process'
const ACTIVE_MATCHING_MESSAGE = 'Finding funding matches...'

export function getScanProgressMessage({
  taskMessage,
  isStale,
  status,
  isStarting,
}: {
  taskMessage: string | null
  isStale: boolean
  status: string
  isStarting: boolean
}) {
  if (taskMessage === RECONNECTING_MESSAGE) return taskMessage
  if (isStale) return 'Scan appears to be stuck...'
  if (taskMessage === LEGACY_STARTING_MESSAGE) return ACTIVE_MATCHING_MESSAGE
  return (
    taskMessage ||
    (status === 'running'
      ? 'Matching...'
      : isStarting
        ? 'Starting scan...'
        : 'Initializing...')
  )
}

interface ScanTaskOptions {
  token: string
  onCompleted?: (results: any[], taskId: string, needId: string) => void
  onError?: (error: Error, taskId: string | null, needId: string) => void
}

export function useScanTask({ token, onCompleted, onError }: ScanTaskOptions) {
  const {
    tasks,
    startTask,
    trackTaskProgress,
    findTaskByNeedId,
    findActiveTaskByNeedId,
    isTaskActive,
    refreshTasksState,
  } = useTaskContext()

  const guardRegen = useProfileRegenGuard()

  // State to track if component is mounted to handle navigation properly
  const isMountedRef = useRef(true)

  // state to track associations (needId -> jobId)
  const [scanJobs, setScanJobs] = useState<Record<string, string>>({})

  // state to track active subscriptions
  const [activeSubscriptions, setActiveSubscriptions] = useState<
    Record<string, { unsubscribe: () => void }>
  >({})

  useEffect(() => {
    // Force refresh when the component using this hook mounts
    refreshTasksState()

    // Set the mounted flag
    isMountedRef.current = true

    // Cleanup when unmounting
    return () => {
      // Unsubscribe from all active subscriptions first
      Object.values(activeSubscriptions).forEach((subscription) => {
        subscription.unsubscribe()
      })

      isMountedRef.current = false
    }
  }, [refreshTasksState, activeSubscriptions])

  // Restore any existing jobs from TaskContext on mount or when tasks are refreshed
  useEffect(() => {
    const existingJobs: Record<string, string> = {}

    // Find all scan tasks with need_id
    Object.values(tasks).forEach((task) => {
      if (
        task.type === 'scan' &&
        task.need_id &&
        task.status !== 'cleaned_up'
      ) {
        existingJobs[task.need_id] = task.id
      }
    })

    // Only update if there are differences
    if (JSON.stringify(existingJobs) !== JSON.stringify(scanJobs)) {
      setScanJobs(existingJobs)
    }
  }, [tasks, scanJobs])

  // track task progress for jobs that need tracking
  useEffect(() => {
    Object.entries(scanJobs).forEach(([, jobId]) => {
      // Get the current task state
      const task = tasks[jobId]

      // Only track if:
      // 1. We don't have an active subscription for this job
      // 2. The task exists
      // 3. We have a token
      // 4. The task is in a state that can be tracked
      if (
        !activeSubscriptions[jobId] &&
        task &&
        token &&
        ['connecting', 'starting', 'running'].includes(task.status)
      ) {
        // The trackTaskProgress now returns a subscription object with an unsubscribe method
        const subscription = trackTaskProgress(jobId, token)

        // Save the subscription
        setActiveSubscriptions(
          (prev) =>
            ({
              ...prev,
              [jobId]: subscription,
            } as Record<string, { unsubscribe: () => void }>)
        )
      }
    })
  }, [scanJobs, tasks, token, trackTaskProgress, activeSubscriptions])

  // monitor task status changes and clean up scanJobs
  useEffect(() => {
    // Check if any tasks referenced in scanJobs have been completed or errored
    Object.entries(scanJobs).forEach(([needId, jobId]) => {
      const task = tasks[jobId]

      // If the task is now cleaned_up, completed, error, or doesn't exist
      if (!task || ['cleaned_up', 'completed', 'error'].includes(task.status)) {
        // For completed or error tasks, we can process their results
        if (task) {
          if (task.status === 'completed' && isMountedRef.current) {
            // Query invalidation is handled directly in TaskContext when tasks complete

            // Still call onCompleted for any component-specific logic
            if (onCompleted) {
              onCompleted([], jobId, needId)
            }
          } else if (
            task.status === 'error' &&
            onError &&
            isMountedRef.current
          ) {
            const errorObj =
              typeof task.error === 'string'
                ? new Error(task.error)
                : task.error || new Error('Unknown error in scan task')

            onError(errorObj, jobId, needId)
          }
        }

        // Only for cleaned_up or non-existent tasks, remove from scanJobs
        if (!task || task.status === 'cleaned_up') {
          setScanJobs((prev) => {
            const newJobs = { ...prev }
            delete newJobs[needId]
            return newJobs
          })
        }

        // If we have an active subscription for this task, unsubscribe and remove it
        if (activeSubscriptions[jobId]) {
          activeSubscriptions[jobId].unsubscribe()

          setActiveSubscriptions((prev) => {
            const newSubscriptions = { ...prev }
            delete newSubscriptions[jobId]
            return newSubscriptions
          })
        }
      }
    })
  }, [tasks, scanJobs, activeSubscriptions, onCompleted, onError])

  // start a new scan
  const startScan = useCallback(
    async (
      needId: string,
      includeRelational = true,
      includeFuturePlanning = false
    ) => {
      if (!needId || !token) return null

      try {
        // check if there's an existing active task for this need
        const existingTask = findActiveTaskByNeedId(needId, 'scan')

        // if there's an existing task, return its ID
        if (existingTask) {
          return existingTask.id
        }

        // start the scan - requires auth token
        const response = await fetch(`${import.meta.env.VITE_API_URL}/scans/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            need_id: needId,
            include_relational: includeRelational,
            include_future_planning: includeFuturePlanning,
          }),
        })

        if (!response.ok) {
          await throwIfProfileRegenerating(response)
          if (response.status === 402) {
            const errorData = await response.json()
            const error = new Error(
              errorData.detail || 'Upgrade required to access this feature'
            )
            error.name = 'PaymentRequired'
            throw error
          }
          throw new Error(`Failed to start scan: ${response.statusText}`)
        }

        const data = await response.json()
        const jobId = data.job_id

        // register in global TaskContext
        startTask('scan', jobId, needId)

        // store the job ID for this need locally - triggers useEffect that handles tracking
        setScanJobs((prev) => ({
          ...prev,
          [needId]: jobId,
        }))

        return jobId
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        // A profile-regen 409 opens the shared block dialog (updating / retry) —
        // don't also surface it as a generic scan error.
        if (guardRegen(err)) return null
        onError?.(err, null, needId)
        return null
      }
    },
    [token, findActiveTaskByNeedId, startTask, onError, guardRegen]
  )

  // reset tracking state for a need
  const resetScanForNeed = useCallback(
    (needId: string) => {
      const jobId = scanJobs[needId]
      if (jobId) {
        // If we have an active subscription, unsubscribe
        if (activeSubscriptions[jobId]) {
          activeSubscriptions[jobId].unsubscribe()

          setActiveSubscriptions((prev) => {
            const newSubscriptions = { ...prev }
            delete newSubscriptions[jobId]
            return newSubscriptions
          })
        }

        // remove from local state
        setScanJobs((prev) => {
          const newJobs = { ...prev }
          delete newJobs[needId]
          return newJobs
        })
      }
    },
    [scanJobs, activeSubscriptions]
  )

  // get job ID for a need
  const getJobIdForNeed = useCallback(
    (needId: string) => {
      // first check local state
      const localJobId = scanJobs[needId]
      if (localJobId) return localJobId

      // then check TaskContext
      const task = findTaskByNeedId(needId, 'scan')
      return task?.id || null
    },
    [scanJobs, findTaskByNeedId]
  )

  // Helper functions that use TaskContext
  const hasActiveScan = useCallback(
    (needId: string) => {
      const task = findActiveTaskByNeedId(needId, 'scan')
      return isTaskActive(task)
    },
    [findActiveTaskByNeedId, isTaskActive]
  )

  const isLoadingForNeed = useCallback(
    (needId: string) => {
      const task = findActiveTaskByNeedId(needId, 'scan')
      return task?.status === 'connecting' || task?.status === 'starting'
    },
    [findActiveTaskByNeedId]
  )

  const errorForNeed = useCallback(
    (needId: string) => {
      // look for both active and completed tasks
      const task = findTaskByNeedId(needId, 'scan')
      return task?.error || null
    },
    [findTaskByNeedId]
  )

  // Status of the need's newest scan, finished or not. statusForNeed only
  // sees active scans, so it can never report 'completed'.
  const latestStatusForNeed = useCallback(
    (needId: string) => {
      const task = findTaskByNeedId(needId, 'scan')
      return task?.status || 'idle'
    },
    [findTaskByNeedId]
  )

  const progressForNeed = useCallback(
    (needId: string) => {
      const task = findActiveTaskByNeedId(needId, 'scan')
      return task?.progress || 0
    },
    [findActiveTaskByNeedId]
  )

  const messageForNeed = useCallback(
    (needId: string) => {
      const task = findActiveTaskByNeedId(needId, 'scan')
      return task?.message || null
    },
    [findActiveTaskByNeedId]
  )

  const statusForNeed = useCallback(
    (needId: string) => {
      const task = findActiveTaskByNeedId(needId, 'scan')
      return task?.status || 'idle'
    },
    [findActiveTaskByNeedId]
  )

  const isScanStaleForNeed = useCallback(
    (needId: string) => {
      const task = findActiveTaskByNeedId(needId, 'scan')
      if (!task) return false

      // if status is already terminal, it's not stale
      if (['completed', 'error', 'cleaned_up'].includes(task.status)) {
        return false
      }

      // check for staleness based on startTime
      const startTime = new Date(task.startTime).getTime()
      const now = new Date().getTime()
      const runningTimeMinutes = (now - startTime) / (1000 * 60)

      return runningTimeMinutes > 15 // 15 minutes timeout
    },
    [findActiveTaskByNeedId]
  )

  const refreshScanState = useCallback(() => {
    refreshTasksState()
  }, [refreshTasksState])

  return {
    startScan,
    getJobIdForNeed,
    hasActiveScan,
    isLoadingForNeed,
    errorForNeed,
    progressForNeed,
    messageForNeed,
    statusForNeed,
    latestStatusForNeed,
    isScanStaleForNeed,
    resetScanForNeed,
    refreshScanState,
  }
}
